// Shared visual primitives for the Zeus kiosk — an "instrument cluster" look
// tuned for a 1280×400 touch bar read from across the room: radial gauges,
// animated bars, sparklines, colour-coded state. No external deps.
import type { CSSProperties, ReactNode } from 'react'
import { C } from '@/components/kiosk/kioskTheme'

// ---------- Panel (tile shell) ----------
export function Panel({
  title, icon, right, onTap, children, accent = C.cyan, style,
}: {
  title: string
  icon?: ReactNode
  right?: ReactNode
  onTap?: () => void
  children: ReactNode
  accent?: string
  style?: CSSProperties
}) {
  return (
    <button
      onClick={onTap}
      className="kpanel group relative flex h-full w-full min-h-0 flex-col overflow-hidden rounded-2xl border text-left transition-[transform,box-shadow] active:scale-[.985]"
      style={{ background: C.panel, borderColor: C.line, ...style }}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)`, opacity: 0.55 }}
      />
      <div className="flex items-center gap-2 px-3.5 pt-2.5 pb-1.5">
        {icon && <span style={{ color: accent }}>{icon}</span>}
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: C.dim }}>{title}</span>
        {right && <span className="ml-auto">{right}</span>}
      </div>
      <div className="min-h-0 flex-1 px-3.5 pb-3">{children}</div>
    </button>
  )
}

// ---------- Animated bar ----------
export function Bar({ value, max = 100, color = C.gaugeFill, height = 8, track = C.gaugeTrack }: {
  value: number | null | undefined
  max?: number
  color?: string
  height?: number
  track?: string
}) {
  const frac = value == null || max <= 0 ? 0 : Math.min(Math.max(value / max, 0), 1)
  return (
    <span className="block w-full overflow-hidden rounded-full" style={{ height, background: track }}>
      <span
        className="block h-full rounded-full"
        style={{
          width: `${frac * 100}%`,
          background: `linear-gradient(90deg, ${color}${C.fillAlpha}, ${color})`,
          transition: 'width .7s cubic-bezier(.4,0,.2,1)',
        }}
      />
    </span>
  )
}

// ---------- Radial gauge (270° arc) ----------
function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const rad = (deg * Math.PI) / 180
  return [cx + r * Math.sin(rad), cy - r * Math.cos(rad)]
}
function arcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const [x0, y0] = polar(cx, cy, r, a0)
  const [x1, y1] = polar(cx, cy, r, a1)
  const large = a1 - a0 > 180 ? 1 : 0
  return `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`
}
export function Gauge({ value, max, color = C.gaugeFill, size = 132, thickness = 9, children }: {
  value: number | null | undefined
  max: number
  color?: string
  size?: number
  thickness?: number
  children?: ReactNode
}) {
  const cx = size / 2
  const r = size / 2 - thickness
  const frac = value == null || max <= 0 ? 0 : Math.min(Math.max(value / max, 0), 1)
  const track = arcPath(cx, cx, r, -135, 135)
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="absolute inset-0">
        <path d={track} fill="none" stroke={C.gaugeTrack} strokeWidth={thickness} strokeLinecap="round" />
        <path
          d={track} fill="none" stroke={color} strokeWidth={thickness} strokeLinecap="round"
          pathLength={100} strokeDasharray={100} strokeDashoffset={100 - frac * 100}
          style={{ transition: 'stroke-dashoffset .8s cubic-bezier(.4,0,.2,1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">{children}</div>
    </div>
  )
}

// ---------- Small full-circle ring ----------
export function Ring({ value, size = 44, thickness = 5, color = C.gaugeFill, children }: {
  value: number | null | undefined
  size?: number
  thickness?: number
  color?: string
  children?: ReactNode
}) {
  const cx = size / 2
  const r = size / 2 - thickness
  const frac = value == null ? 0 : Math.min(Math.max(value / 100, 0), 1)
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="absolute inset-0 -rotate-90">
        <circle cx={cx} cy={cx} r={r} fill="none" stroke={C.gaugeTrack} strokeWidth={thickness} />
        <circle
          cx={cx} cy={cx} r={r} fill="none" stroke={color} strokeWidth={thickness} strokeLinecap="round"
          pathLength={100} strokeDasharray={100} strokeDashoffset={100 - frac * 100}
          style={{ transition: 'stroke-dashoffset .7s cubic-bezier(.4,0,.2,1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  )
}

// ---------- Sparkline (area) ----------
export function Spark({ points, color = C.gaugeFill, width = 200, height = 40 }: {
  points: (number | null)[]
  color?: string
  width?: number
  height?: number
}) {
  const vals = points.map((p) => (p == null ? null : p))
  const nums = vals.filter((v): v is number => v != null)
  if (nums.length < 2) return null
  const min = Math.min(...nums)
  const max = Math.max(...nums)
  const span = max - min || 1
  const n = vals.length
  const x = (i: number) => (i / (n - 1)) * width
  const y = (v: number) => height - 4 - ((v - min) / span) * (height - 8)
  let d = ''
  let started = false
  vals.forEach((v, i) => {
    if (v == null) return
    d += `${started ? 'L' : 'M'} ${x(i).toFixed(1)} ${y(v).toFixed(1)} `
    started = true
  })
  const last = vals.reduce<number>((acc, v, i) => (v != null ? i : acc), 0)
  const area = `${d} L ${x(last).toFixed(1)} ${height} L 0 ${height} Z`
  const gid = `sp${Math.round(width)}${Math.round(color.charCodeAt(1))}`
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

// ---------- Colour chip ----------
export function Chip({ children, color = C.dim }: { children: ReactNode; color?: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums"
      style={{ color, background: `${color}1a`, border: `1px solid ${color}33` }}
    >
      {children}
    </span>
  )
}

// ---------- Live status dot ----------
export function Dot({ color, pulse = false }: { color: string; pulse?: boolean }) {
  return (
    <span className="relative inline-flex h-2.5 w-2.5">
      {pulse && <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ background: color }} />}
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ background: color }} />
    </span>
  )
}
